import React, { useState, useRef, useEffect } from 'react';
import { hot } from 'react-hot-loader';
import Button from '../../components/Elements/Button/Button';
import Checkbox from '../../components/Elements/Checkbox/Checkbox';
import { purifier, getCookie, setCookie, fixBackgroundScroll } from '../../utils/dom';
import { translate } from '../../utils/locale';
import { FOCUSABLE, isAllowedForCountry, useFocusTrap } from '../../utils/notification';
import s from './CookiesNotification.scss';

// Cookie JSON of a stored decision (browser only), or null. Read after mount - never during the first render.
const readStoredDecision = () => {
	try {
		const cookies = getCookie('cookies', true);
		return Array.isArray(cookies) ? cookies : null;
	} catch (err) {
		return null;
	}
};

// TEMPORARY consent logging (see /cookie-consent-status in server/index.js). Fire and forget - must never break the banner.
const logConsent = (body) => {
	try {
		fetch('/cookie-consent-status', { method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).catch(() => {});
	} catch (err) {
		// ignore
	}
};

const CookiesNotification = ({ content, setup, cookieConsent, hasCookieDecision = false, reshowNotification = false }) => {
	// The first render must match the server HTML exactly (hydration), so it only uses the Redux state the server rendered from.
	// A decision cookie the server didn't receive (e.g. SameSite) is reconciled right after mount below.
	const [showCookie, toggleCookie] = useState(() => !hasCookieDecision);
	const [showManageSettings, toggleManageSettings] = useState(false);
	const [cookieSettings, updateCookieSettings] = useState(['all']);
	const wrapperRef = useRef(null);
	const isInitialMount = useRef(true);
	// Whether analytics_storage is currently granted on this page - the server sets it from the stored
	// decision (see consent default in server/render.js), so a reshown banner doesn't replay the PageView.
	const analyticsGrantedRef = useRef(false);

	// Reset to the initial (visible, top-level) state whenever a "manage cookie preferences"
	// link clears the stored consent and flips reshowNotification (see actions/environment.js).
	// Skipped on mount — effects with a dependency array also fire once after the first render,
	// which would otherwise stomp the initial showCookie value above and force the banner open
	// even when a decision already exists.
	useEffect(() => {
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}
		toggleCookie(true);
		toggleManageSettings(false);
		updateCookieSettings(['all']);
	}, [reshowNotification]);

	// One 'visit' per full page load - effects only run in the browser, so bots that don't execute JS aren't counted.
	// bannerShown = visitor had no decision yet and the banner applies to their country.
	useEffect(() => {
		const decided = hasCookieDecision || !!readStoredDecision();
		const bannerShown = !decided && content.some((item) => item?.text && isAllowedForCountry(item, setup));
		logConsent({ event: 'visit', country: setup.country, decided, bannerShown });
	}, []);

	useEffect(() => {
		const initialDecision = readStoredDecision();
		analyticsGrantedRef.current = !!initialDecision && (initialDecision.indexOf('all') !== -1 || initialDecision.indexOf('analytics') !== -1);

		if (hasCookieDecision) return;
		const storedDecision = initialDecision;
		if (!storedDecision) return;
		toggleCookie(false);
		cookieConsent(storedDecision);
	}, []);

	useEffect(() => {
		if (!showCookie) return undefined;
		fixBackgroundScroll(true);
		return () => fixBackgroundScroll(false);
	}, [showCookie]);

	// Don't steal focus on load; the visitor's first Tab press is pulled into the banner (onto the
	// accept button) and then kept within whichever controls are showing. No Escape - a choice is required.
	useFocusTrap(wrapperRef, showCookie, { autoFocus: false, initialFocus: '[data-cookie-initial-focus] button' });

	useEffect(() => {
		if (!showManageSettings || !wrapperRef.current) return;
		const managePanel = wrapperRef.current.querySelector('[data-cookie-manage-panel]');
		const managePanelFocusEl = managePanel && managePanel.querySelector(FOCUSABLE);
		if (managePanelFocusEl) managePanelFocusEl.focus();
	}, [showManageSettings]);

	if (!showCookie) return null;

	const manageSettingsHandler = (e) => {
		e.preventDefault();
		toggleManageSettings(true);
		if (cookieSettings.indexOf('all') !== -1) updateCookieSettings(['default']);
	};

	const updateCookieSettingsHandler = (id, value) => {
		if (id === 'default') return;
		if (cookieSettings.indexOf('all') !== -1) updateCookieSettings(['default']);
		if (value && cookieSettings.indexOf(id) === -1) updateCookieSettings((prevState) => [...prevState, id]);
		if (!value && cookieSettings.indexOf(id) !== -1) updateCookieSettings((prevState) => [...prevState.slice(0, prevState.indexOf(id)), ...prevState.slice(prevState.indexOf(id) + 1)]);
	};

	const notifications = content.filter((item) => item?.text && isAllowedForCountry(item, setup));

	if (notifications.length === 0) return null;

	return (
		<div className={s.notification} ref={wrapperRef} role="dialog" aria-modal="true" aria-label="Cookie preferences">
			<div className={s.notification_screen} role="presentation" />
			{
				notifications.map((item) => {
					const manageFields = (item.manageFields && item.manageFields.filter((field) => field.key)) || [];

					const removeNotificationHandler = (e, override = false) => {
						e.preventDefault();

						// Consent signals must be built from what was actually saved - after "Manage" the local
						// cookieSettings is ['default'], so building from it made "Accept all" send denied.
						const acceptedAll = cookieSettings.indexOf('all') !== -1 || cookieSettings.length === manageFields.length || override;
						const savedSettings = acceptedAll ? ['all'] : cookieSettings;
						setCookie('cookies', savedSettings, acceptedAll ? item.cookieDuration : item.managedCookieDuration);
						cookieConsent(savedSettings);
						logConsent({ event: 'decision', country: setup.country, permissions: savedSettings });

						const analyticsGranted = acceptedAll || savedSettings.indexOf('analytics') !== -1;
						const marketingGranted = acceptedAll || savedSettings.indexOf('marketing') !== -1;
						const consent = {};

						if (typeof window.ecomid !== 'undefined') {
							window.ecomid.getConsent = () => ({
								preferences: true,
								statistics: analyticsGranted,
								marketing: marketingGranted,
							});
						}

						if (typeof window.gtag !== 'undefined') {
							consent.functionality_storage = 'granted';
							consent.security_storage = 'granted';
							consent.ad_storage = marketingGranted ? 'granted' : 'denied';
							consent.ad_user_data = marketingGranted ? 'granted' : 'denied';
							consent.ad_personalization = marketingGranted ? 'granted' : 'denied';
							consent.analytics_storage = analyticsGranted ? 'granted' : 'denied';
							consent.personalization_storage = 'granted';

							window.gtag('consent', 'update', consent);
							if (typeof window.dataLayer === 'undefined') window.dataLayer = [];
							window.dataLayer.push({
								event: 'ConsentUpdated',
								values: {},
							});

							// The current page's PageView (routesMap.js) fired before the choice, so it went out
							// cookieless. Replay it once when analytics goes from denied to granted, so the
							// landing page counts for visitors who accept.
							if (analyticsGranted && !analyticsGrantedRef.current) {
								window.dataLayer.push({
									event: 'PageView',
									values: {
										path: window.location.pathname,
									},
								});
							}
						}

						analyticsGrantedRef.current = analyticsGranted;

						toggleCookie(false);
					};

					const acceptAllHandler = (e) => {
						e.preventDefault();
						removeNotificationHandler(e, true);
					};

					return (
						<div key={item.name} className={[s.notification_inner, showManageSettings ? s.notification_inner__manage : null].filter(Boolean).join(' ')}>
							<div className={s.notification_inner_container}>
								{
									showManageSettings ? (
										<>
											<div className={s.notification_inner_container__manage} data-cookie-manage-panel>
												<h4>{ translate(setup, item.manageTitle) }</h4>
												{
													manageFields.map((field) => (
														<Checkbox variantClasses={['checkbox__cookies']} key={field.key} id={field.cookieKey} selected={field.cookieKey === 'default' || cookieSettings.indexOf(field.cookieKey) !== -1} inputChange={updateCookieSettingsHandler}>
															<h5>{ translate(setup, field.title) }</h5>
															<span dangerouslySetInnerHTML={{ __html: purifier(translate(setup, field.content), { ALLOWED_TAGS: ['a'] }) }} />
														</Checkbox>
													))
												}
												<div className={s.notification_inner_container_submit}>
													<Button type="button" variantClasses={['button__fullWidth', 'button__secondary']} clickEventHandler={acceptAllHandler}>{ translate(setup, item.acceptAllText) }</Button>
													<Button type="button" variantClasses={['button__fullWidth']} clickEventHandler={removeNotificationHandler}>{ translate(setup, item.saveManageFields) }</Button>
												</div>
											</div>
										</>
									) : (
										<>
											<div className={s.notification_inner_container_text} dangerouslySetInnerHTML={{ __html: purifier(translate(setup, item?.text), { ALLOWED_TAGS: ['a'] }) }} />
											<div className={s.notification_inner_container_button} data-cookie-initial-focus>
												<Button type="button" variantClasses={['button__fullWidth']} clickEventHandler={removeNotificationHandler}>{ translate(setup, item.removeText) }</Button>
											</div>
											<div className={s.notification_inner_container_button}>
												<Button type="button" variantClasses={['button__fullWidth', 'button__secondary']} clickEventHandler={manageSettingsHandler}>{ translate(setup, item.manageText) }</Button>
											</div>
										</>
									)
								}
							</div>
						</div>
					);
				})
			}
		</div>
	);
};

export default hot(module)(CookiesNotification);
